import Fuse from 'fuse.js';

class DataService {
  constructor() {
    // Updated Google Sheet URL
    this.baseUrl = 'https://docs.google.com/spreadsheets/d/1wYlGGnbEJEDX1Yaijh_vy8iMkfCiSuQtt2AalGwGeXQ';
    this.sheetId = '977534709';
  }

  async fetchGoogleSheet() {
    try {
      const csvUrl = `${this.baseUrl}/export?format=csv&gid=${this.sheetId}`;
      const response = await fetch(csvUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch sheet: ${response.status}`);
      }
      
      const csvText = await response.text();
      return this.parseCSV(csvText);
    } catch (error) {
      console.error('Error fetching Google Sheet:', error);
      throw new Error('Unable to fetch product data. Please check your connection.');
    }
  }

  parseCSV(csvText) {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length === 0) return [];
    
    const headers = this.parseCSVLine(lines[0]);
    const products = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i]);
      if (values.length >= headers.length) {
        const product = {};
        headers.forEach((header, index) => {
          product[header.toLowerCase().trim()] = values[index] || '';
        });
        
        // Add unique ID
        product.id = `product_${i}`;
        
        // Only add products with a name (not empty in column A)
        if (product.name && product.name.trim() !== '') {
          products.push(product);
        }
      }
    }
    
    return products;
  }

  parseCSVLine(line) {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  }

  async fetchAndProcessData(progressCallback) {
    try {
      progressCallback(10, 'Fetching product data from Google Sheets...');
      const rawProducts = await this.fetchGoogleSheet();
      
      if (!rawProducts || rawProducts.length === 0) {
        throw new Error('No products found in the sheet');
      }
      
      progressCallback(30, 'Processing product information...');
      
      // Extract all unique categories
      const allCategories = new Set();
      rawProducts.forEach(product => {
        if (product.categories) {
          const categories = product.categories.split(',').map(cat => cat.trim());
          categories.forEach(cat => {
            if (cat) allCategories.add(cat);
          });
        }
      });
      
      const products = [];
      const batchSize = 40; // Increased batch size for faster processing
      let processed = 0;
      
      // Process products in batches to avoid overwhelming the browser
      for (let i = 0; i < rawProducts.length; i += batchSize) {
        const batch = rawProducts.slice(i, i + batchSize);
        const batchPromises = batch.map(async (product) => {
          try {
            // Create a consolidated search text field that combines all relevant fields
            // Updated to include content from columns D, E, F
            const searchFields = [
              product.name || '',
              product.sku || '',
              product.tagline || '',
              product.content_column_d || '', // New content column D
              product.content_column_e || '', // New content column E 
              product.content_column_f || '', // New content column F
              product.categories || '',
            ];
            
            // Join all fields together for easy searching
            const searchText = searchFields
              .join(' ')
              .toLowerCase()
              .replace(/\s+/g, ' ')
              .trim();
            
            // Combine content from D, E, F columns for search purposes
            const combinedContent = [
              product.content_column_d || '',
              product.content_column_e || '',
              product.content_column_f || ''
            ].join(' ').trim();
            
            return {
              id: product.id,
              name: product.name || '',
              sku: product.sku || '',
              url: product.url || '',
              tagline: product.tagline || '',
              content: combinedContent, // Combined content from columns D, E, F
              categories: product.categories || '',
              logo: product.product_image || product['product image'] || '', // Updated column name
              featured: product.featured?.toLowerCase() === 'true',
              searchText: searchText // This field will be used for searching
            };
          } catch (error) {
            console.warn(`Error processing product ${product.id}:`, error);
            return {
              id: product.id,
              name: product.name || '',
              sku: product.sku || '',
              url: product.url || '',
              tagline: product.tagline || '',
              content: '',
              categories: product.categories || '',
              logo: product.product_image || product['product image'] || '',
              featured: product.featured?.toLowerCase() === 'true',
              searchText: product.name || '' // Fallback to just the name
            };
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        products.push(...batchResults);
        processed += batch.length;
        
        const progress = 30 + (processed / rawProducts.length) * 50;
        progressCallback(progress, `Processed ${processed}/${rawProducts.length} products...`);
        
        // Very small delay to prevent browser freezing
        if (i + batchSize < rawProducts.length) {
          await new Promise(resolve => setTimeout(resolve, 5));
        }
      }
      
      progressCallback(85, 'Building search index...');
      
      // Create search index with more flexible matching
      const searchIndex = new Fuse(products, {
        keys: [
          { name: 'searchText', weight: 1 },
        ],
        threshold: 0.4, // More lenient threshold for better results
        includeScore: true,
        includeMatches: true,
        minMatchCharLength: 2,
        ignoreLocation: true, // This makes the search more flexible
        useExtendedSearch: true
      });
      
      progressCallback(100, 'Ready!');
      
      return {
        products,
        searchIndex,
        categories: Array.from(allCategories).sort()
      };
    } catch (error) {
      console.error('Error in fetchAndProcessData:', error);
      throw error;
    }
  }

  search(searchIndex, query, products) {
    if (!searchIndex || !query || query.trim().length < 2) {
      return [];
    }
    
    const searchQuery = query.trim().toLowerCase();
    
    // First try a direct search on the searchText field
    const directMatches = products.filter(product => {
      return product.searchText.toLowerCase().includes(searchQuery);
    });
    
    // If we have enough direct matches, return them
    if (directMatches.length > 0) {
      return directMatches;
    }
    
    // Otherwise, use Fuse.js for fuzzy matching
    const fuseResults = searchIndex.search(searchQuery);
    return fuseResults
      .filter(result => result.score < 0.6) // More lenient threshold for results
      .map(result => result.item);
  }

  filterByCategory(products, category) {
    if (!category || category === 'All Categories') {
      return products;
    }
    
    return products.filter(product => {
      if (!product.categories) return false;
      const categories = product.categories.split(',').map(cat => cat.trim());
      return categories.includes(category);
    });
  }

  // Add spell checking capability
  checkSpelling(query, products) {
    if (!query || query.length < 3) return null;
    
    // Simple function to calculate Levenshtein distance (edit distance)
    const levenshteinDistance = (str1, str2) => {
      const track = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
      
      for (let i = 0; i <= str1.length; i++) {
        track[0][i] = i;
      }
      
      for (let j = 0; j <= str2.length; j++) {
        track[j][0] = j;
      }
      
      for (let j = 1; j <= str2.length; j++) {
        for (let i = 1; i <= str1.length; i++) {
          const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
          track[j][i] = Math.min(
            track[j][i - 1] + 1, // deletion
            track[j - 1][i] + 1, // insertion
            track[j - 1][i - 1] + indicator // substitution
          );
        }
      }
      
      return track[str2.length][str1.length];
    };
    
    // Extract common terms from products
    const commonTerms = new Set();
    products.forEach(product => {
      if (product.name) {
        product.name.split(/\s+/).forEach(word => {
          if (word.length > 3) commonTerms.add(word.toLowerCase());
        });
      }
      
      if (product.tagline) {
        product.tagline.split(/\s+/).forEach(word => {
          if (word.length > 3) commonTerms.add(word.toLowerCase());
        });
      }
      
      if (product.sku) {
        commonTerms.add(product.sku.toLowerCase());
      }
    });
    
    // Check if query is close to any common term
    const queryWords = query.toLowerCase().split(/\s+/);
    const correctedWords = queryWords.map(word => {
      // Skip short words or exact matches
      if (word.length < 3 || commonTerms.has(word)) return word;
      
      let bestMatch = null;
      let minDistance = Infinity;
      
      // Calculate distance to each common term
      commonTerms.forEach(term => {
        // Skip if length difference is too large
        if (Math.abs(term.length - word.length) > 3) return;
        
        const distance = levenshteinDistance(word, term);
        
        // The distance threshold depends on word length
        const threshold = Math.min(2, Math.floor(word.length / 3));
        if (distance <= threshold && distance < minDistance) {
          minDistance = distance;
          bestMatch = term;
        }
      });
      
      return bestMatch || word;
    });
    
    const corrected = correctedWords.join(' ');
    return corrected !== query.toLowerCase() ? corrected : null;
  }

  // Get suggestions for search autocomplete
  getSuggestions(query, products, searchIndex) {
    if (!query || query.length < 2) return [];
    
    const suggestions = [];
    const lowerQuery = query.toLowerCase();
    
    // Check for spelling corrections
    const correctedQuery = this.checkSpelling(query, products);
    if (correctedQuery) {
      suggestions.push({
        text: correctedQuery,
        originalText: query,
        isCorrection: true
      });
    }
    
    // Look for direct matches in products
    const matchingProducts = products.filter(product => 
      (product.name && product.name.toLowerCase().includes(lowerQuery)) ||
      (product.sku && product.sku.toLowerCase().includes(lowerQuery)) ||
      (product.tagline && product.tagline.toLowerCase().includes(lowerQuery))
    ).slice(0, 5);
    
    // Add product-specific suggestions
    matchingProducts.forEach(product => {
      if (product.name && product.name.toLowerCase().includes(lowerQuery)) {
        suggestions.push({
          text: product.name,
          isCorrection: false
        });
      }
    });
    
    // Deduplicate suggestions
    const uniqueSuggestions = [];
    const seen = new Set();
    suggestions.forEach(suggestion => {
      if (!seen.has(suggestion.text.toLowerCase())) {
        seen.add(suggestion.text.toLowerCase());
        uniqueSuggestions.push(suggestion);
      }
    });
    
    return uniqueSuggestions.slice(0, 5); // Limit to 5 suggestions
  }
}

export const dataService = new DataService();